import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  Typography,
  Tooltip,
} from '@mui/material';
import NavigateBeforeRoundedIcon from '@mui/icons-material/NavigateBeforeRounded';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import {
  addDays,
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isValid,
  parse,
  startOfMonth,
  startOfWeek,
  subMonths,
  subWeeks,
} from 'date-fns';

import appointmentDateIcon from '@/assets/images/appointments/appointment-date.svg';
import AppointmentForm, { AppointmentFormValues } from '@/components/appointments/AppointmentForm';
import NotificationCenter from '@/components/notifications/NotificationCenter';
import ScheduleAppointmentDialog from '@/components/appointments/ScheduleAppointmentDialog';
import Modal from '@/components/shared/Modal';
import InlineFeedback from '@/components/shared/InlineFeedback';
import { ClientNameLink } from '@/components/clients/ClientNameLink';
import { useAppointmentCalendar, useAppointmentDialog } from '@/hooks/appointments';
import { getAppointments } from '@/lib/api/appointments';
import {
  getTimeOff,
  getWeeklyAvailabilitySchedule,
  type TimeOff,
  type WeeklyAvailabilitySchedule,
  type AvailabilityWeekday,
} from '@/lib/api/availability';
import { createAppointmentAction, getAppointmentFormDefaultValues, isDateInTimeOff } from '@/utils/appointments';

type CalendarMode = 'week' | 'month';

type CalendarAppointment = {
  id: string;
  clientId: string;
  title: string;
  subtitle: string;
  startTime: Date;
  endTime: Date;
  paletteIndex: number;
};

type RawAppointment = {
  id?: unknown;
  _id?: unknown;
  title?: unknown;
  type?: unknown;
  appointmentType?: unknown;
  mode?: unknown;
  location?: unknown;
  status?: unknown;
  duration?: unknown;
  startTime?: unknown;
  start_time?: unknown;
  endTime?: unknown;
  end_time?: unknown;
  date?: unknown;
  clientName?: unknown;
  client_name?: unknown;
  clientId?: unknown;
  client_id?: unknown;
  client?: {
    name?: unknown;
    fullName?: unknown;
    firstName?: unknown;
    lastName?: unknown;
  } | null;
};

const pageTitleSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: { xs: '26px', md: '38px' },
  lineHeight: { xs: '115%', md: '100%' },
  letterSpacing: 0,
  color: '#1e293b',
} as const;

const pageSubtitleSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: { xs: '14px', sm: '16px' },
  lineHeight: { xs: '20px', sm: '22px' },
  color: '#667085',
} as const;

const eventPalettes = [
  { bg: '#EFE9FF', border: '#D8C9FF', text: '#4C1D95' },
  { bg: '#FEEFD8', border: '#F5D6A4', text: '#92400E' },
  { bg: '#DFF8F0', border: '#AEEBD9', text: '#0F766E' },
  { bg: '#FFE4EA', border: '#F8B7C6', text: '#BE123C' },
] as const;

const timeSlots = Array.from({ length: 24 }, (_, index) => index);
const weekdayLabels = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;

function parseDateValue(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const parsed = new Date(String(value));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function toSentenceCase(input: string): string {
  if (!input) return input;
  return input.charAt(0).toUpperCase() + input.slice(1).toLowerCase();
}

function getModeLabel(raw: RawAppointment): string {
  const type = String(raw.appointmentType || raw.mode || '').toLowerCase();
  const location = String(raw.location || '').toLowerCase();

  if (type === 'offline' || type === 'in_person' || location.includes('person')) {
    return 'In-Person';
  }

  return 'Video Call';
}

function getClientLabel(raw: RawAppointment): string {
  const direct = String(raw.clientName || raw.client_name || '').trim();
  if (direct) return direct;

  const nestedName = String(raw.client?.name || raw.client?.fullName || '').trim();
  if (nestedName) return nestedName;

  const firstName = String(raw.client?.firstName || '').trim();
  const lastName = String(raw.client?.lastName || '').trim();
  const combined = `${firstName} ${lastName}`.trim();
  if (combined) return combined;

  return '';
}

function hashText(text: string): number {
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = (hash << 5) - hash + text.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash);
}

function normalizeAppointments(rawAppointments: RawAppointment[]): CalendarAppointment[] {
  return rawAppointments
    .map((raw, index) => {
      const startTime = parseDateValue(raw.startTime || raw.start_time || raw.date);
      if (!startTime) return null;

      const explicitEnd = parseDateValue(raw.endTime || raw.end_time);
      const fallbackDurationRaw = Number(raw.duration);
      const fallbackDuration = Number.isFinite(fallbackDurationRaw) && fallbackDurationRaw > 0
        ? fallbackDurationRaw
        : 60;
      const endTime = explicitEnd || new Date(startTime.getTime() + fallbackDuration * 60_000);

      const status = String(raw.status || '').toLowerCase();
      if (status === 'cancelled' || status === 'completed') return null;

      const clientLabel = getClientLabel(raw);
      const titleText = String(raw.title || raw.type || '').trim();
      const title = clientLabel || titleText || 'Appointment';
      const subtitle = titleText && clientLabel
        ? titleText
        : getModeLabel(raw);

      const id = String(raw.id || raw._id || `calendar-appointment-${index}`);
      const clientId = String(raw.clientId ?? raw.client_id ?? '').trim();

      return {
        id,
        clientId,
        title,
        subtitle: toSentenceCase(subtitle),
        startTime,
        endTime,
        paletteIndex: hashText(id) % eventPalettes.length,
      };
    })
    .filter((appointment): appointment is CalendarAppointment => Boolean(appointment));
}

const AppointmentsCalendarViewPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [calendarMode, setCalendarMode] = useState<CalendarMode>('week');
  const [focusedDate, setFocusedDate] = useState<Date>(new Date());
  const [appointments, setAppointments] = useState<CalendarAppointment[]>([]);
  const [schedule, setSchedule] = useState<WeeklyAvailabilitySchedule | null>(null);
  const [timeOffEntries, setTimeOffEntries] = useState<TimeOff[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [schedulePrefill, setSchedulePrefill] = useState<{ date?: Date; time?: string } | undefined>();
  const [appointmentModalFeedback, setAppointmentModalFeedback] = useState<{ title: string; message: string } | null>(null);

  const {
    selectedDate,
    selectedTime,
    setSelectedDate,
    setSelectedTime,
  } = useAppointmentCalendar();

  const {
    createDialogOpen,
    selectedAppointment,
    updateCreateDialogState,
    setSelectedAppointment,
  } = useAppointmentDialog(selectedDate, setSelectedDate, selectedTime, setSelectedTime, {
    syncCreateFromUrl: false,
  });

  useEffect(() => {
    if (searchParams.get('create') !== 'true') return;

    const dateParam = searchParams.get('date');
    const timeParam = searchParams.get('time');

    let prefillDate: Date | undefined;
    if (dateParam) {
      const parsed = parse(dateParam, 'yyyy-MM-dd', new Date());
      if (isValid(parsed)) {
        prefillDate = parsed;
      }
    }

    setSchedulePrefill({
      date: prefillDate,
      time: timeParam ?? undefined,
    });
    setIsScheduleDialogOpen(true);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('create');
    nextParams.delete('date');
    nextParams.delete('time');
    setSearchParams(nextParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const navigateToAppointmentDetail = useCallback(
    (event: React.SyntheticEvent, appointment: CalendarAppointment) => {
      event.stopPropagation();
      const cid = appointment.clientId.trim();
      if (!cid) return;
      navigate(
        `/clients/${cid}/details-v2/appointments?session=${encodeURIComponent(appointment.id)}`,
      );
    },
    [navigate],
  );

  const weekStart = useMemo(
    () => startOfWeek(focusedDate, { weekStartsOn: 1 }),
    [focusedDate],
  );

  const navigateToCreateAppointment = useCallback(
    (day: Date, hour: number) => {
      const now = new Date();
      const slotDate = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour, 0, 0, 0);
      const clickedDayNorm = new Date(day.getFullYear(), day.getMonth(), day.getDate());
      const todayNorm = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const isPastDay = clickedDayNorm < todayNorm;
      const isPastTime = isSameDay(clickedDayNorm, todayNorm) && slotDate < now;

      let d = day;
      let h = hour;
      if (isPastDay || isPastTime) {
        d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const nextHour = new Date(now);
        nextHour.setHours(now.getHours() + 1, 0, 0, 0);
        h = nextHour.getHours();
      }

      const timeStr = `${String(h).padStart(2, '0')}:00`;
      setSchedulePrefill({ date: d, time: timeStr });
      setIsScheduleDialogOpen(true);
    },
    [],
  );

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)),
    [weekStart],
  );

  const monthStart = useMemo(() => startOfMonth(focusedDate), [focusedDate]);
  const monthDays = useMemo(
    () =>
      eachDayOfInterval({
        start: startOfWeek(monthStart, { weekStartsOn: 1 }),
        end: endOfWeek(endOfMonth(monthStart), { weekStartsOn: 1 }),
      }),
    [monthStart],
  );

  const fetchCalendarAppointments = useCallback(async () => {
    const rangeStart =
      calendarMode === 'week'
        ? weekStart
        : startOfWeek(startOfMonth(focusedDate), { weekStartsOn: 1 });

    const rangeEnd =
      calendarMode === 'week'
        ? endOfWeek(weekStart, { weekStartsOn: 1 })
        : endOfWeek(endOfMonth(focusedDate), { weekStartsOn: 1 });

    const [appointmentsResponse, scheduleResponse, timeOffResponse] = await Promise.all([
      getAppointments({
        page: 1,
        limit: 1000,
        startDate: rangeStart.toISOString(),
        endDate: rangeEnd.toISOString(),
      }).catch(e => {
        console.error('Failed to fetch appointments:', e);
        return { appointments: [] };
      }),
      getWeeklyAvailabilitySchedule().catch(e => {
        console.error('Failed to fetch availability schedule:', e);
        return null;
      }),
      getTimeOff().catch(e => {
        console.error('Failed to fetch time off:', e);
        return [] as TimeOff[];
      }),
    ]);

    const normalized = normalizeAppointments((appointmentsResponse?.appointments || []) as RawAppointment[]);
    return { normalized, scheduleResponse, timeOffResponse };
  }, [calendarMode, focusedDate, weekStart]);

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      try {
        setIsLoading(true);
        const { normalized, scheduleResponse, timeOffResponse } = await fetchCalendarAppointments();
        if (!isMounted) return;
        setAppointments(normalized);
        if (scheduleResponse) {
          setSchedule(scheduleResponse);
        }
        setTimeOffEntries(timeOffResponse);
      } catch (error) {
        console.error('Failed to fetch calendar data:', error);
        if (isMounted) {
          setAppointments([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void run();

    return () => {
      isMounted = false;
    };
  }, [fetchCalendarAppointments]);

  const reloadCalendarAppointments = useCallback(async () => {
    try {
      setIsLoading(true);
      const { normalized, scheduleResponse, timeOffResponse } = await fetchCalendarAppointments();
      setAppointments(normalized);
      if (scheduleResponse) {
        setSchedule(scheduleResponse);
      }
      setTimeOffEntries(timeOffResponse);
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
      setAppointments([]);
    } finally {
      setIsLoading(false);
    }
  }, [fetchCalendarAppointments]);

  const handleCreateAppointment = useCallback(
    async (data: AppointmentFormValues) => {
      const scope = data.updateScope || 'this_only';
      setAppointmentModalFeedback(null);
      await createAppointmentAction(
        data,
        selectedAppointment,
        updateCreateDialogState,
        setSelectedAppointment,
        setSelectedTime,
        reloadCalendarAppointments,
        scope,
        {
          suppressSuccessToast: true,
          onError: (title, message) => setAppointmentModalFeedback({ title, message }),
        },
      );
    },
    [
      selectedAppointment,
      updateCreateDialogState,
      setSelectedAppointment,
      setSelectedTime,
      reloadCalendarAppointments,
    ],
  );

  const weekAppointmentMap = useMemo(() => {
    const map = new Map<string, CalendarAppointment[]>();

    appointments.forEach((appointment) => {
      const key = `${format(appointment.startTime, 'yyyy-MM-dd')}-${appointment.startTime.getHours()}`;
      const existing = map.get(key) || [];
      existing.push(appointment);
      map.set(key, existing);
    });

    return map;
  }, [appointments]);

  const monthAppointmentMap = useMemo(() => {
    const map = new Map<string, CalendarAppointment[]>();

    appointments.forEach((appointment) => {
      const key = format(appointment.startTime, 'yyyy-MM-dd');
      const existing = map.get(key) || [];
      existing.push(appointment);
      map.set(key, existing);
    });

    return map;
  }, [appointments]);

  const handleShiftPeriod = (direction: 'prev' | 'next') => {
    if (calendarMode === 'week') {
      setFocusedDate((current) => (direction === 'prev' ? subWeeks(current, 1) : addWeeks(current, 1)));
      return;
    }

    setFocusedDate((current) => (direction === 'prev' ? subMonths(current, 1) : addMonths(current, 1)));
  };

  const periodLabel = calendarMode === 'week'
    ? `${format(weekDays[0], 'MMMM')} ${format(weekDays[0], 'yyyy')}`
    : format(focusedDate, 'MMMM yyyy');

  return (
    <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: { xs: '16px', sm: '24px', lg: '28px' },
          border: '1px solid #dbe6eb',
          bgcolor: '#f8fafc',
          maxWidth: '100%',
        }}
      >
        <Box sx={{ px: { xs: 1.5, sm: 3, md: 4 }, pt: { xs: 1.75, sm: 2.5 } }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: '16px',
              border: '1px solid #dbe6eb',
              bgcolor: '#fafcfe',
              px: { xs: 1.5, sm: 3 },
              py: 1.5,
            }}
          >
            <Breadcrumbs separator="/" aria-label="breadcrumb">
              <Link
                component={RouterLink}
                underline="hover"
                color="inherit"
                to="/appointments"
                sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}
              >
                Appointments
              </Link>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#0f172a' }}>
                Calendar View
              </Typography>
            </Breadcrumbs>
          </Paper>
        </Box>

        <Box sx={{ px: { xs: 1.5, sm: 3, md: 4 }, pb: { xs: 4, md: 5 }, pt: { xs: 2.5, sm: 4 } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 2,
              flexDirection: { xs: 'column', md: 'row' },
            }}
          >
            <Box sx={{ display: 'grid', gap: 0.95 }}>
              <Typography sx={pageTitleSx}>Appointments</Typography>
              <Typography sx={pageSubtitleSx}>{format(focusedDate, 'EEE dd MMM')}</Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', md: 'auto' } }}>
              <Button
                disableElevation
                onClick={() => {
                  setSchedulePrefill(undefined);
                  setIsScheduleDialogOpen(true);
                }}
                sx={{
                  borderRadius: '10px',
                  px: 2,
                  minHeight: 40,
                  textTransform: 'none',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.84rem',
                  bgcolor: '#3BAEDB',
                  color: '#fff',
                  width: { xs: '100%', md: 'auto' },
                  '&:hover': {
                    bgcolor: '#2A9ECF',
                  },
                }}
              >
                New Appointment +
              </Button>
              <NotificationCenter buttonVariant="feature" />

              <Button
                sx={{
                  minWidth: 40,
                  width: 40,
                  height: 40,
                  borderRadius: '14px',
                  bgcolor: '#fff',
                  border: '1px solid #D0D5DD',
                  color: '#475467',
                  '&:hover': {
                    bgcolor: '#F8FAFC',
                    borderColor: '#BFC7D2',
                  },
                }}
              >
                <Box
                  component="img"
                  src={appointmentDateIcon}
                  alt=""
                  aria-hidden
                  sx={{ width: 22, height: 22, objectFit: 'contain', display: 'block' }}
                />
              </Button>
            </Box>
          </Box>

          <Paper
            elevation={0}
            sx={{
              mt: 3,
              borderRadius: '20px',
              border: '1px solid #E4E7EC',
              bgcolor: '#FCFDFE',
              p: { xs: 1.5, md: 2.25 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', md: 'center' },
                justifyContent: 'space-between',
                gap: 1.2,
                flexDirection: { xs: 'column', md: 'row' },
                mb: 1.8,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                <IconButton onClick={() => handleShiftPeriod('prev')} sx={{ color: '#667085' }}>
                  <NavigateBeforeRoundedIcon />
                </IconButton>
                <Box sx={{ minWidth: 125 }}>
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 500,
                      fontSize: '0.88rem',
                      color: '#98A2B3',
                      lineHeight: 1,
                    }}
                  >
                    {format(focusedDate, 'yyyy')}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.2,
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: { xs: '1.6rem', md: '2rem' },
                      lineHeight: 1,
                      color: '#1D2939',
                    }}
                  >
                    {periodLabel.split(' ')[0]}
                  </Typography>
                </Box>
                <IconButton onClick={() => handleShiftPeriod('next')} sx={{ color: '#667085' }}>
                  <NavigateNextRoundedIcon />
                </IconButton>
              </Box>

              <Box
                sx={{
                  p: 0.4,
                  borderRadius: '10px',
                  bgcolor: '#F2F4F7',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.4,
                }}
              >
                <Button
                  disableElevation
                  onClick={() => setCalendarMode('week')}
                  sx={{
                    minHeight: 34,
                    px: 1.5,
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    color: calendarMode === 'week' ? '#344054' : '#667085',
                    bgcolor: calendarMode === 'week' ? '#fff' : 'transparent',
                    border: calendarMode === 'week' ? '1px solid #EAECF0' : '1px solid transparent',
                    '&:hover': {
                      bgcolor: calendarMode === 'week' ? '#fff' : '#F9FAFB',
                    },
                  }}
                >
                  Week
                </Button>
                <Button
                  disableElevation
                  onClick={() => setCalendarMode('month')}
                  sx={{
                    minHeight: 34,
                    px: 1.5,
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    color: calendarMode === 'month' ? '#344054' : '#667085',
                    bgcolor: calendarMode === 'month' ? '#fff' : 'transparent',
                    border: calendarMode === 'month' ? '1px solid #EAECF0' : '1px solid transparent',
                    '&:hover': {
                      bgcolor: calendarMode === 'month' ? '#fff' : '#F9FAFB',
                    },
                  }}
                >
                  Month
                </Button>
              </Box>
            </Box>

            {isLoading ? (
              <Box sx={{ py: 8, display: 'grid', placeItems: 'center', gap: 1.25 }}>
                <CircularProgress size={28} />
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.88rem', color: '#667085' }}>
                  Loading calendar...
                </Typography>
              </Box>
            ) : calendarMode === 'week' ? (
              <Box
                sx={{
                  border: '1px solid #E4E7EC',
                  borderRadius: '14px',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  bgcolor: '#fff',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                <Box sx={{ minWidth: { xs: 720, md: 0 } }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '52px repeat(7, minmax(0, 1fr))', md: '68px repeat(7, minmax(0, 1fr))' },
                    bgcolor: '#FCFDFE',
                  }}
                >
                  <Box sx={{ minHeight: 62 }} />
                  {weekDays.map((day, index) => {
                    const isToday = isSameDay(day, new Date());
                    return (
                      <Box
                        key={day.toISOString()}
                        sx={{
                          minHeight: 62,
                          px: 0.8,
                          py: 0.7,
                          display: 'grid',
                          placeItems: 'center',
                          gap: 0.2,
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 600,
                            fontSize: '0.76rem',
                            color: '#667085',
                            letterSpacing: '0.04em',
                          }}
                        >
                          {weekdayLabels[index]}
                        </Typography>
                        <Box
                          sx={{
                            minWidth: 34,
                            height: 34,
                            borderRadius: '999px',
                            bgcolor: isToday ? '#0F172A' : 'transparent',
                            color: isToday ? '#fff' : '#344054',
                            display: 'grid',
                            placeItems: 'center',
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Inter, sans-serif',
                              fontWeight: 600,
                              fontSize: '0.96rem',
                              color: 'inherit',
                            }}
                          >
                            {format(day, 'd')}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>

                <Box sx={{ maxHeight: 650, overflowY: 'auto' }}>
                  {timeSlots.map((hour, rowIndex) => (
                    <Box
                      key={hour}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '52px repeat(7, minmax(0, 1fr))', md: '68px repeat(7, minmax(0, 1fr))' },
                        minHeight: 36,
                        borderBottom: rowIndex === timeSlots.length - 1 ? 'none' : '1px solid #F2F4F7',
                      }}
                    >
                      <Box
                        sx={{
                          px: 1,
                          py: 1,
                          borderRight: '1px solid #EEF2F6',
                          bgcolor: '#FCFDFE',
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 500,
                            fontSize: '0.84rem',
                            color: '#667085',
                          }}
                        >
                          {`${String(hour).padStart(2, '0')}:00`}
                        </Typography>
                      </Box>

                      {weekDays.map((day, dayIndex) => {
                        const key = `${format(day, 'yyyy-MM-dd')}-${hour}`;
                        const dayAppointments = weekAppointmentMap.get(key) || [];

                        const isTimeOffDay = isDateInTimeOff(day, timeOffEntries);
                        let isAvailable = false;
                        if (!isTimeOffDay && schedule) {
                          const weekdayMap: AvailabilityWeekday[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                          const dayStr = weekdayMap[day.getDay()];
                          const daySched = schedule.days.find(d => d.day === dayStr);
                          if (daySched && daySched.isEnabled) {
                            isAvailable = daySched.slots.some(slot => {
                              const startHour = parseInt(slot.startTime.split(':')[0], 10);
                              const endHour = parseInt(slot.endTime.split(':')[0], 10);
                              // We consider the hour cell available if any part of the hour is within a slot
                              // If a slot is 9:00 - 17:00, then hour 9 is available because 9 >= 9 && 9 < 17
                              return hour >= startHour && hour < endHour;
                            });
                          }
                        }
                        const unavailableTooltip = isTimeOffDay
                          ? 'Time off'
                          : 'You are not available during this time';

                        const appointmentBlocks = (
                          <Box sx={{ display: 'grid', gap: 0.35 }}>
                            {dayAppointments.map((appointment) => {
                              const palette = eventPalettes[appointment.paletteIndex];
                              return (
                                <Paper
                                  key={appointment.id}
                                  elevation={0}
                                  onClick={(e) => navigateToAppointmentDetail(e, appointment)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      navigateToAppointmentDetail(e, appointment);
                                    }
                                  }}
                                  role={appointment.clientId.trim() ? 'button' : undefined}
                                  tabIndex={appointment.clientId.trim() ? 0 : undefined}
                                  sx={{
                                    borderRadius: '10px',
                                    border: `1px solid ${palette.border}`,
                                    bgcolor: palette.bg,
                                    px: 0.75,
                                    py: 0.65,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontFamily: 'Inter, sans-serif',
                                      fontWeight: 700,
                                      fontSize: '0.78rem',
                                      lineHeight: 1.2,
                                      color: palette.text,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    <ClientNameLink clientId={appointment.clientId} className="text-inherit hover:text-inherit">
                                      {appointment.title}
                                    </ClientNameLink>
                                  </Typography>
                                  <Typography
                                    sx={{
                                      mt: 0.2,
                                      fontFamily: 'Inter, sans-serif',
                                      fontWeight: 500,
                                      fontSize: '0.75rem',
                                      color: '#475467',
                                    }}
                                  >
                                    {appointment.subtitle}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      mt: 0.16,
                                      fontFamily: 'Inter, sans-serif',
                                      fontWeight: 500,
                                      fontSize: '0.75rem',
                                      color: '#667085',
                                    }}
                                  >
                                    {`${format(appointment.startTime, 'HH:mm')} - ${format(appointment.endTime, 'HH:mm')}`}
                                  </Typography>
                                </Paper>
                              );
                            })}
                          </Box>
                        );

                        const renderWeekSlotCell = () => (
                          <Box
                            role="button"
                            tabIndex={0}
                            onClick={() => navigateToCreateAppointment(day, hour)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                navigateToCreateAppointment(day, hour);
                              }
                            }}
                            sx={{
                              px: 0.4,
                              py: 0.45,
                              borderRight: dayIndex === weekDays.length - 1 ? 'none' : '1px solid #EEF2F6',
                              bgcolor: isAvailable ? '#fff' : '#F8FAFC',
                              height: '100%',
                              cursor: 'pointer',
                              '&:hover': {
                                bgcolor: isAvailable ? '#F9FAFB' : '#F1F5F9',
                              },
                            }}
                          >
                            {appointmentBlocks}
                          </Box>
                        );

                        return (
                          <React.Fragment key={key}>
                            {isAvailable ? (
                              renderWeekSlotCell()
                            ) : (
                              <Tooltip title={unavailableTooltip} placement="top" arrow>
                                {renderWeekSlotCell()}
                              </Tooltip>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </Box>
                  ))}
                </Box>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  border: '1px solid #E4E7EC',
                  borderRadius: '14px',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  bgcolor: '#fff',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                <Box sx={{ minWidth: { xs: 560, md: 0 } }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                    borderBottom: '1px solid #E4E7EC',
                    bgcolor: '#FCFDFE',
                  }}
                >
                  {weekdayLabels.map((label) => (
                    <Typography
                      key={label}
                      sx={{
                        py: 1.2,
                        textAlign: 'center',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: '0.76rem',
                        color: '#667085',
                        letterSpacing: '0.04em',
                        borderRight: label === 'SUN' ? 'none' : '1px solid #EEF2F6',
                      }}
                    >
                      {label}
                    </Typography>
                  ))}
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
                  {monthDays.map((day, dayIndex) => {
                    const key = format(day, 'yyyy-MM-dd');
                    const dayAppointments = monthAppointmentMap.get(key) || [];
                    const visible = dayAppointments.slice(0, 2);
                    const hiddenCount = dayAppointments.length - visible.length;

                    const weekdayMap: AvailabilityWeekday[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                    const dayStr = weekdayMap[day.getDay()];
                    const daySchedule = schedule?.days.find(d => d.day === dayStr);

                    const isTimeOffDay = isDateInTimeOff(day, timeOffEntries);
                    const isAvailable = !isTimeOffDay && (daySchedule?.isEnabled ?? true);
                    const monthUnavailableTooltip = isTimeOffDay
                      ? 'Time off'
                      : 'You are not available on this day';

                    const renderMonthDayCell = () => (
                      <Box
                        role="button"
                        tabIndex={0}
                        onClick={() => navigateToCreateAppointment(day, 9)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigateToCreateAppointment(day, 9);
                          }
                        }}
                        sx={{
                          minHeight: 120,
                          p: 0.75,
                          borderRight: dayIndex % 7 === 6 ? 'none' : '1px solid #EEF2F6',
                          borderBottom: dayIndex >= monthDays.length - 7 ? 'none' : '1px solid #EEF2F6',
                          bgcolor: isSameMonth(day, monthStart) ? (isAvailable ? '#fff' : '#F8FAFC') : '#F1F5F9',
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: isSameMonth(day, monthStart)
                              ? isAvailable
                                ? '#F9FAFB'
                                : '#F1F5F9'
                              : '#E8EDF3',
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            color: isSameMonth(day, monthStart) ? '#344054' : '#98A2B3',
                          }}
                        >
                          {format(day, 'd')}
                        </Typography>

                        <Box sx={{ mt: 0.5, display: 'grid', gap: 0.38 }}>
                          {visible.map((appointment) => {
                            const palette = eventPalettes[appointment.paletteIndex];
                            return (
                              <Box
                                key={`${appointment.id}-month`}
                                onClick={(e) => navigateToAppointmentDetail(e, appointment)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    navigateToAppointmentDetail(e, appointment);
                                  }
                                }}
                                role={appointment.clientId.trim() ? 'button' : undefined}
                                tabIndex={appointment.clientId.trim() ? 0 : undefined}
                                sx={{
                                  width: '100%',
                                  px: 1,
                                  py: 0.36,
                                  minHeight: 30,
                                  borderRadius: '20px / 12px',
                                  border: `1px solid ${palette.border}`,
                                  bgcolor: palette.bg,
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontFamily: 'Inter, sans-serif',
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    lineHeight: 1.2,
                                    color: palette.text,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  <ClientNameLink clientId={appointment.clientId} className="text-inherit hover:text-inherit">
                                    {appointment.title}
                                  </ClientNameLink>
                                </Typography>
                              </Box>
                            );
                          })}

                          {hiddenCount > 0 && (
                            <Typography
                              sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                color: '#667085',
                                px: 0.12,
                              }}
                            >
                              +{hiddenCount} more
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );

                    return (
                      <React.Fragment key={key}>
                        {isAvailable ? (
                          renderMonthDayCell()
                        ) : (
                          <Tooltip title={monthUnavailableTooltip} placement="top" arrow>
                            {renderMonthDayCell()}
                          </Tooltip>
                        )}
                      </React.Fragment>
                    );
                  })}
                </Box>
                </Box>
              </Box>
            )}
          </Paper>
        </Box>
      </Paper>

      <ScheduleAppointmentDialog
        open={isScheduleDialogOpen}
        onClose={() => {
          setIsScheduleDialogOpen(false);
          setSchedulePrefill(undefined);
        }}
        prefill={schedulePrefill}
        onAppointmentCreated={reloadCalendarAppointments}
      />

      <Modal
        open={createDialogOpen && !!selectedAppointment}
        onOpenChange={(open) => {
          updateCreateDialogState(open);
          if (!open) {
            setAppointmentModalFeedback(null);
            setSelectedAppointment(null);
            setSelectedTime(undefined);
          }
        }}
        title="Reschedule/Update Appointment"
        description="Update the appointment details below. Changes will be synced to your calendar."
        maxWidth="xl"
      >
        {appointmentModalFeedback ? (
          <div className="mb-4">
            <InlineFeedback
              variant="error"
              title={appointmentModalFeedback.title}
              message={appointmentModalFeedback.message}
            />
          </div>
        ) : null}
        <AppointmentForm
          key={selectedAppointment?.id || `new-${selectedTime || Date.now()}`}
          onSubmit={handleCreateAppointment}
          defaultValues={getAppointmentFormDefaultValues(
            selectedAppointment,
            selectedDate,
            selectedTime,
          )}
          disabledDates={[]}
          isEditing={!!selectedAppointment}
          isRecurring={selectedAppointment?.isRecurring || false}
          isModified={false}
          isParent={false}
          appointmentId={selectedAppointment?.id}
          appointment={selectedAppointment ?? undefined}
        />
      </Modal>
    </Box>
  );
};

export default AppointmentsCalendarViewPage;
